import moment from "moment";
import { addLocale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from "react";
import { check_status_all, update_check_status } from "../Helper/links";
import API from "../Helper/server/API";
import empty from '../public/svg/empty.svg';
import { Dialog } from 'primereact/dialog';

function CheckStatus() {
    const toast = useRef(null);
    addLocale('ar', {
        firstDayOfWeek: 6,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        today: 'اليوم',
        clear: 'تفريغ',
    });

    const [dateCheck, setDateCheck] = useState(new Date());
    const [reservationsCheckIn, setReservationsCheckIn] = useState([]);
    const [reservationsCheckOut, setReservationsCheckOut] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('in'); // 'in' or 'out'
    const [qrInput, setQrInput] = useState('');
    const qrInputRef = useRef(null);

    // Scanner state
    const [showScannerDialog, setShowScannerDialog] = useState(false);
    const [scannerHtml5, setScannerHtml5] = useState(null);

    const getReservations = async (date, checkType) => {
        setLoading(true);
        const response = await API.get(`${check_status_all}?date_check=${moment(date).format('YYYY-MM-DD')}&check=${checkType}`);
        if (response?.success === 1) {
            if (checkType === 'out') {
                setReservationsCheckOut(response.data);
            } else {
                setReservationsCheckIn(response.data);
            }
        } else {
            toast.current?.show({ severity: 'error', summary: 'خطاء', detail: response?.msg || 'حدث خطأ في جلب البيانات' });
        }
        setLoading(false);
    };

    useEffect(() => {
        getReservations(dateCheck, 'in');
        getReservations(dateCheck, 'out');
    }, [dateCheck]);

    const handleCheckAction = async (qrcode, actionType) => {
        if (!qrcode) return;
        setLoading(true);
        const response = await API.get(`${update_check_status}?qrcode=${qrcode}&action=${actionType}`);
        setLoading(false);
        setQrInput('');

        if (response?.success === 1) {
            toast.current?.show({ severity: 'success', summary: 'نجاح', detail: response.msg });
            // Refresh data
            getReservations(dateCheck, actionType);
        } else {
            toast.current?.show({ severity: 'error', summary: 'خطاء', detail: response?.msg || 'لم يتم العثور على الحجز' });
        }
        if (qrInputRef.current) qrInputRef.current.focus();
    };

    const handleQrKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCheckAction(qrInput, activeTab);
        }
    };

    const startCameraScanner = () => {
        setShowScannerDialog(true);
        setTimeout(() => {
            const html5QrCode = new window.Html5Qrcode("reader_check_status");
            setScannerHtml5(html5QrCode);
            html5QrCode.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 }
                },
                (decodedText) => {
                    html5QrCode.stop();
                    setShowScannerDialog(false);
                    handleCheckAction(decodedText, activeTab);
                },
                (errorMessage) => {
                    // ignore errors during scanning
                }
            ).catch((err) => {
                console.log(err);
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'تعذر الوصول إلى الكاميرا' });
            });
        }, 300);
    };

    const stopCameraScanner = () => {
        if (scannerHtml5) {
            scannerHtml5.stop().catch(err => console.log(err));
        }
        setShowScannerDialog(false);
    };

    const StatusTemplate = (rowData) => {
        const isChecked = activeTab === 'in' ? rowData.is_checked_in : rowData.is_checked_out;
        if (isChecked === 1) {
            return <span className="badge badge-success px-3 py-2">تم {activeTab === 'in' ? 'الدخول' : 'المغادرة'}</span>;
        }
        return <span className="badge badge-warning px-3 py-2">انتظار</span>;
    };

    const TypeTemplate = (rowData) => {
        if (rowData.reservation_type === 'service') {
            return <span className="badge badge-info px-3 py-2">خدمة: {rowData.service_name}</span>;
        }
        return <span className="badge badge-primary px-3 py-2">إقامة: {rowData.type === 'chalet' ? 'شاليه' : (rowData.type === 'apartment' ? 'شقة' : rowData.type)} - {rowData.number}</span>;
    };

    const ActionTemplate = (rowData) => {
        const isChecked = activeTab === 'in' ? rowData.is_checked_in : rowData.is_checked_out;
        if (isChecked === 1) return null;

        return (
            <Button
                icon="pi pi-check-circle"
                label={activeTab === 'in' ? "تسجيل دخول" : "تسجيل مغادرة"}
                className="p-button-sm p-button-success"
                onClick={() => handleCheckAction(rowData.qrcode, activeTab)}
            />
        );
    };

    return (
        <div className="page-wrapper" dir="rtl">
            <div className="content-wrapper">
                <Toast ref={toast} position="top-center" style={{ fontFamily: "Cairo" }} />
                <div className="page-heading">
                    <h1 className="page-title">إدارة الدخول والمغادرة (Check In / Out)</h1>
                </div>

                <div className="card p-4 mb-4">
                    <div className="row align-items-center">
                        <div className="col-md-4">
                            <label className="font-weight-bold">تاريخ السجل</label>
                            <Calendar
                                locale="ar"
                                value={dateCheck}
                                onChange={(e) => setDateCheck(e.value)}
                                dateFormat="yy-mm-dd"
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-8 text-left mt-4 mt-md-0 d-flex justify-content-end align-items-center gap-2">
                            <span className="p-input-icon-right w-50">
                                <i className="pi pi-qrcode" />
                                <InputText
                                    ref={qrInputRef}
                                    placeholder="استخدم قارئ الباركود هنا..."
                                    value={qrInput}
                                    onChange={(e) => setQrInput(e.target.value)}
                                    onKeyDown={handleQrKeyDown}
                                    className="w-100"
                                    autoFocus
                                />
                            </span>
                            <Button
                                icon="pi pi-camera"
                                className="p-button-rounded p-button-secondary mr-2"
                                onClick={startCameraScanner}
                                tooltip="استخدام الكاميرا"
                            />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <ul className="nav nav-tabs tabs-line nav-fill">
                        <li className="nav-item">
                            <a className={`nav-link ${activeTab === 'in' ? 'active' : ''}`} href="#tab-in" onClick={(e) => { e.preventDefault(); setActiveTab('in'); if (qrInputRef.current) qrInputRef.current.focus(); }}>
                                <h6 className="mb-0 py-2"><i className="pi pi-sign-in ml-2"></i>تسجيل الدخـــول (Arrivals)</h6>
                            </a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link ${activeTab === 'out' ? 'active' : ''}`} href="#tab-out" onClick={(e) => { e.preventDefault(); setActiveTab('out'); if (qrInputRef.current) qrInputRef.current.focus(); }}>
                                <h6 className="mb-0 py-2"><i className="pi pi-sign-out ml-2"></i>تسجيل المغــادرة (Departures)</h6>
                            </a>
                        </li>
                    </ul>

                    <div className="tab-content mt-4">
                        <div className={`tab-pane fade ${activeTab === 'in' ? 'show active' : ''}`} id="tab-in">
                            <DataTable
                                value={reservationsCheckIn}
                                loading={loading}
                                paginator rows={15}
                                emptyMessage={<center><img src={empty} alt="empty" width="10%" className="m-4" /><h3>لايــوجد حجــوزات للدخول اليوم</h3></center>}
                                style={{ fontFamily: "Cairo", direction: "rtl" }}
                            >
                                <Column field="ID_reservation" header="رقم الحجز" sortable style={{ width: '10%' }}></Column>
                                <Column field="visitor_name" header="العميــل" sortable style={{ width: '20%' }}></Column>
                                <Column header="نوع الحجز" body={TypeTemplate} style={{ width: '20%' }}></Column>
                                <Column field="date_check_in" header="تاريخ الدخول" style={{ width: '15%' }}></Column>
                                <Column header="الحالة" body={StatusTemplate} style={{ width: '15%' }}></Column>
                                <Column header="إجراء" body={ActionTemplate} style={{ width: '20%' }}></Column>
                            </DataTable>
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'out' ? 'show active' : ''}`} id="tab-out">
                            <DataTable
                                value={reservationsCheckOut}
                                loading={loading}
                                paginator rows={15}
                                emptyMessage={<center><img src={empty} alt="empty" width="10%" className="m-4" /><h3>لايــوجد حجــوزات للمغادرة اليوم</h3></center>}
                                style={{ fontFamily: "Cairo", direction: "rtl" }}
                            >
                                <Column field="ID_reservation" header="رقم الحجز" sortable style={{ width: '10%' }}></Column>
                                <Column field="visitor_name" header="العميــل" sortable style={{ width: '20%' }}></Column>
                                <Column header="نوع الحجز" body={TypeTemplate} style={{ width: '20%' }}></Column>
                                <Column field="date_check_out" header="تاريخ المغادرة" style={{ width: '15%' }}></Column>
                                <Column header="الحالة" body={StatusTemplate} style={{ width: '15%' }}></Column>
                                <Column header="إجراء" body={ActionTemplate} style={{ width: '20%' }}></Column>
                            </DataTable>
                        </div>
                    </div>
                </div>

                <Dialog
                    visible={showScannerDialog}
                    onHide={stopCameraScanner}
                    header="مسح رمز الاستجابة السريعة (QR Code)"
                    style={{ width: '500px', fontFamily: 'Cairo' }}
                >
                    <div id="reader_check_status" style={{ width: '100%' }}></div>
                    <p className="text-center mt-3">قم بتوجيه الكاميرا نحو رمز QR الخاص بالحجز.</p>
                </Dialog>
            </div>
        </div>
    );
}

export default CheckStatus;
